import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { EmailHubService } from 'src/app/private/services/email-hub.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-mail-left-actions',
  templateUrl: './mail-left-actions.component.html',
  styleUrls: ['./mail-left-actions.component.scss']
})
export class MailLeftActionsComponent {
  isSmallScreen = false;

  @Input() activeAction: string = 'inbox';
  @Input() isAccountNotConnected = false;
  @Input() mailProvider: 'outlook' | 'gmail' = 'outlook';
  @Output() actionChange = new EventEmitter<string>();
  @Output() refresh = new EventEmitter<void>();
  @Output() isCompose = new EventEmitter<void>();
  @Output() changeMailbox = new EventEmitter<void>();
  @Input() updateDraft = false;
  btnLoading = false;

  openRevokeAccessModal = false;


  actions = [
    {
      label: 'Inbox',
      icon: 'inbox',
      action: 'inbox'
    },
    {
      label: 'Sent',
      icon: 'paper-plane',
      action: 'sent'
    },
    {
      label: 'Drafts',
      icon: 'file-alt',
      action: 'drafts'
    },
    {
      label: 'Junk',
      icon: 'archive',
      action: 'trash'
    },
    {
      label:'Deleted',
      icon:'trash',
      action:'deleted'
    }
  ]

  constructor(
    private breakpointObserver: BreakpointObserver,
    private bottomSheet: MatBottomSheet,
    private toast: ToastrService,
    private api: EmailHubService,
    private usersService: UsersService,
    private localStorage: LocalStorageService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.breakpointObserver.observe(['(max-width: 780px)']).subscribe((result) => {
      this.isSmallScreen = result.matches;
    });
  }

  setActiveAction(action: string) {
    this.actionChange.emit(action);
  }

  reloadData() {
    this.refresh.emit();
  }

  openBottomSheet(): void {
    this.isCompose.emit();
  }

  handlOpenRevokeAccessModal(content: any) {
    this.openRevokeAccessModal = true;
     this.modalService.open(content, {
      size: 'l',
      scrollable: true,
      centered: true,
    });
  }

  
  confirmRevokeAccess() {
    if (this.btnLoading) {
      return;
    }
    /** Must match the sidebar provider — not `getMailHubUserEmail()` (that follows “active” / session). */
    const linkedEmail =
      this.mailProvider === 'gmail'
        ? this.localStorage.getMailboxEmail('gmail')
        : this.localStorage.getMailboxEmail('outlook');
    if (!linkedEmail) {
      this.toast.error('No linked mailbox found for this provider.');
      return;
    }

    this.btnLoading = true;

    if (this.mailProvider === 'gmail') {
      this.api
        .disconnectGmail({ hospital_id: this.localStorage.getHospitalId(), email_id: linkedEmail })
        .pipe(finalize(() => (this.btnLoading = false)))
        .subscribe({
          next: () => {
            this.toast.success('Gmail access revoked successfully');
            this.modalService.dismissAll();
            this.localStorage.removeMailboxConnection('gmail');
            this.persistCommPrefToUserThenReload();
          },
          error: (e) => {
            console.error(e);
            this.toast.error('Error revoking Gmail access');
          }
        });
      return;
    }

    this.api
      .revokeAccess({
        email: linkedEmail,
        hospital_id: this.localStorage.getHospitalId()
      })
      .pipe(finalize(() => (this.btnLoading = false)))
      .subscribe({
        next: () => {
          this.toast.success('Access revoked successfully');
          this.modalService.dismissAll();
          this.localStorage.removeMailboxConnection('outlook');
          this.persistCommPrefToUserThenReload();
        },
        error: (e) => {
          console.error(e);
          this.toast.error('Error revoking access');
        }
      });
  }

  onChangeMailbox(): void {
    this.changeMailbox.emit();
  }

  /**
   * Writes updated v2 pref to the user row so Communication settings / next login match,
   * then reloads so any cached UI (e.g. client settings) picks up localStorage + DB.
   */
  private persistCommPrefToUserThenReload(): void {
    const userId = this.localStorage.getUserId();
    if (userId == null || userId === '') {
      setTimeout(() => window.location.reload(), 0);
      return;
    }
    this.usersService
      .updateUser({
        user_id: userId,
        commincation_pref: this.localStorage.getCommPrefPayloadForUser()
      })
      .subscribe({
        next: () => setTimeout(() => window.location.reload(), 0),
        error: (err) => {
          console.error('persistCommPrefToUserThenReload failed', err);
          setTimeout(() => window.location.reload(), 0);
        }
      });
  }
}
@Component({
  selector: 'bottom-sheet',
  templateUrl: './bottom-sheet.component.html',
  styleUrls: ['./mail-left-actions.component.scss'],
  imports: [CommonModule,
    // CKEditorComponent,
    FormsModule
  ],
  // standalone: true
  standalone: true
})
export class BottomSheetActionsComponent implements AfterViewInit {

  openBCC = false;
  openCC = false;
  editorData: string = '';

  // Rich editor state
  @ViewChild('bsEditorElement') bsEditorElement!: ElementRef;
  bsBoldActive = false;
  bsItalicActive = false;
  bsUnderlineActive = false;
  bsStrikeActive = false;
  bsBulletActive = false;
  bsOrderedActive = false;
  files: any[] = [];
  to: string = '';
  cc: string = '';
  bcc: string = '';
  subject: string = '';
  btnLoading = false;

  @Output() refresh = new EventEmitter<void>();



  constructor(
    private bottomSheetRef: MatBottomSheetRef<BottomSheetActionsComponent>,
    private toast: ToastrService,
    private api: EmailHubService,
    private localStorage: LocalStorageService
  ) {}

  ngAfterViewInit() {
    // editor starts empty for new compose
  }

  // ─── Rich Editor Methods ──────────────────────────────────────────────────

  updateBsFormattingState() {
    try { this.bsBoldActive = document.queryCommandState('bold'); } catch {}
    try { this.bsItalicActive = document.queryCommandState('italic'); } catch {}
    try { this.bsUnderlineActive = document.queryCommandState('underline'); } catch {}
    try { this.bsStrikeActive = document.queryCommandState('strikeThrough'); } catch {}
    try { this.bsBulletActive = document.queryCommandState('insertUnorderedList'); } catch {}
    try { this.bsOrderedActive = document.queryCommandState('insertOrderedList'); } catch {}
  }

  applyBsFormat(cmd: string) {
    if (!this.bsEditorElement) { return; }
    document.execCommand(cmd, false);
    this.bsEditorElement.nativeElement.focus();
    this.updateBsFormattingState();
    this.syncBsEditorContent();
  }

  applyBsFontSize(event: any) {
    const val = event.target.value;
    if (val) { document.execCommand('fontSize', false, val); this.bsEditorElement?.nativeElement.focus(); this.syncBsEditorContent(); }
    event.target.value = '';
  }

  applyBsHeading(event: any) {
    const val = event.target.value;
    if (val) { document.execCommand('formatBlock', false, val); this.bsEditorElement?.nativeElement.focus(); this.syncBsEditorContent(); }
    event.target.value = '';
  }

  applyBsColor(event: any, cmd: string) {
    document.execCommand(cmd, false, event.target.value);
    this.bsEditorElement?.nativeElement.focus();
    this.syncBsEditorContent();
  }

  applyBsLink() {
    const url = prompt('Enter URL (include https://):', 'https://');
    if (url && url !== 'https://') { document.execCommand('createLink', false, url); }
    this.bsEditorElement?.nativeElement.focus();
    this.syncBsEditorContent();
  }

  clearBsFormatting() {
    document.execCommand('removeFormat', false);
    document.execCommand('formatBlock', false, 'div');
    this.bsBoldActive = this.bsItalicActive = this.bsUnderlineActive =
      this.bsStrikeActive = this.bsBulletActive = this.bsOrderedActive = false;
    this.bsEditorElement?.nativeElement.focus();
    this.syncBsEditorContent();
  }

  onBsEditorInput() { this.syncBsEditorContent(); this.updateBsFormattingState(); }
  onBsEditorBlur() { this.syncBsEditorContent(); }

  syncBsEditorContent() {
    if (!this.bsEditorElement) { return; }
    this.editorData = this.bsEditorElement.nativeElement.innerHTML;
  }

  // ─────────────────────────────────────────────────────────────────────────

  selectAction(action: string): void {
    this.bottomSheetRef.dismiss(action);
  }

  openLink(event: MouseEvent): void {
    this.bottomSheetRef.dismiss();
    event.preventDefault();
  }

  handleOpenBCC() {
    this.openBCC = !this.openBCC;
  }

  handleOpenCC() {
    this.openCC = !this.openCC;
  }

  onFileSelected(event: any) {
    const selectedFiles: FileList = event.target.files;
    console.log('Selected Files:', selectedFiles);
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const reader = new FileReader();
      console.log('File:', file);
      reader.onload = (e: any) => {
        this.files.push({ file, preview: e.target.result });
        console.log('Files:', this.files);
      };

      reader.readAsDataURL(file);  // Generate preview
    }
  }

  formatSize(size: number): string {
    if (size < 1024) return `${size} B`;
    else if (size < 1048576) return `${(size / 1024).toFixed(2)} KB`;
    else return `${(size / 1048576).toFixed(2)} MB`;
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }
  

  closeBottomSheet() {
    this.bottomSheetRef.dismiss();
  }

  sendMail() {
    if (!this.to) {
      this.toast.error('Please enter a recipient email address');
      return;
    }

    if (!this.subject) {
      this.toast.error('Please enter a subject');
      return;
    }

    if (!this.editorData) {
      this.toast.error('Please enter a message');
      return;
    }

    console.log('Sending email...');
    this.btnLoading = true;

    let attachments:any[] = [];

    this.files.forEach((file) => {
      attachments.push({
        "@odata.type": "#microsoft.graph.fileAttachment",
        "name": file.file.name,
        "contentType": file.file.type,
        "contentBytes": file.preview
      });
    });
 
    const param = {
      subject: this.subject,
      contentType: 'HTML',
      bodyContent: this.editorData,
      recipientEmail: this.to,
      fromEmail: this.localStorage.getMailHubUserEmail(),
      attachments: attachments
    }

    this.api.sendEmailUsingOutlook(param).subscribe({
      next: (res) => {
        
        this.refresh.emit();
        
        setTimeout(() => {
          this.btnLoading = false;
        this.closeBottomSheet();
        this.toast.success('Email sent successfully');
        }, 1500);
        
      },
      error: (e) => {
        console.error(e);
        this.toast.error('Error sending email');
        this.btnLoading = false;
      }
    })
  

    
  }



}
